import React, { useEffect, useMemo, useState } from 'react';
import cn from '@/utils/cn';
import SPAButton from './SPAButton';

// An image slot on an edit page (program images, the unit's student text image and video poster):
// thumbnail, label, caption, and a Choose File button that is also a drop target.
const ACCEPT = 'image/png,image/jpeg,image/gif';
const ACCEPTED_TYPES = ACCEPT.split(',');
const REJECTED_MESSAGE = 'That file isn’t a PNG, JPG, or GIF.';

const hasFiles = (event: React.DragEvent) => Array.from(event.dataTransfer.types).includes('Files');

// A preview URL for a picked file, released when the file changes.
const useObjectUrl = (file: File | undefined) => {
  const url = useMemo(
    () => (file && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null),
    [file],
  );

  useEffect(() => () => {
    if (url) URL.revokeObjectURL(url);
  }, [url]);

  return url;
};

interface ThumbnailProps {
  alt: string;
  // The saved image at full size; the thumbnail links to it in a new tab. A pending pick has none.
  href?: string | null;
  src: string | null;
}

const thumbnailClass = cn(
  'tw-flex tw-h-12 tw-w-12 tw-shrink-0 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-md',
  'tw-border tw-border-solid tw-border-spa-border-secondary tw-bg-spa-bg-tertiary',
);

const Thumbnail = ({ alt, href, src }: ThumbnailProps) => {
  const picture = src
    ? <img alt={alt} className="tw-h-full tw-w-full tw-object-contain" src={src} />
    : <span aria-hidden="true" className="tw-text-xs tw-text-spa-tertiary">—</span>;

  if (!href) return <span className={thumbnailClass}>{picture}</span>;

  return (
    <a
      aria-label={`Open ${alt} at full size`}
      className={cn(thumbnailClass, 'hover:tw-border-spa-accent focus-visible:tw-border-spa-accent')}
      href={href}
      rel="noreferrer"
      target="_blank"
    >
      {picture}
    </a>
  );
};

export interface RowImage {
  file_name: string | null;
  thumb_url: string | null;
  url: string | null;
}

interface ImageRowProps {
  image: RowImage;
  inputId: string;
  label: string;
  pending?: File;
  onPick?: (file: File | undefined) => void;
}

// An uploadable row is also a drop target: it lights up while a file is dragged over it and takes
// the drop as the pending file. dragleave fires when the cursor crosses into a child, so the
// highlight only clears when the cursor leaves the row itself.
export const ImageRow = ({
  image, inputId, label, pending, onPick,
}: ImageRowProps) => {
  const previewUrl = useObjectUrl(pending);
  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState(false);

  const take = (file: File | undefined) => {
    const accepted = !!file && ACCEPTED_TYPES.includes(file.type);
    setRejected(!!file && !accepted);
    if (accepted) onPick?.(file);
  };

  const dragHandlers = onPick && {
    onDragEnter: (event: React.DragEvent) => {
      if (hasFiles(event)) setDragging(true);
    },
    onDragLeave: (event: React.DragEvent) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false);
    },
    onDragOver: (event: React.DragEvent) => {
      if (hasFiles(event)) event.preventDefault();
    },
    onDrop: (event: React.DragEvent) => {
      event.preventDefault();
      setDragging(false);
      take(event.dataTransfer.files[0]);
    },
  };

  const caption = pending ? `${pending.name} (not saved yet)` : image.file_name ?? 'No image uploaded';

  return (
    <div
      className={cn(
        'tw-flex tw-items-center tw-gap-4 tw-rounded-md tw-py-3 tw-transition-colors',
        onPick && '-tw-mx-3 tw-px-3 tw-border tw-border-dashed',
        dragging ? 'tw-border-spa-accent tw-bg-spa-accent-bg-subtle' : 'tw-border-transparent',
      )}
      {...dragHandlers}
    >
      <Thumbnail
        alt={pending ? `New ${label}` : label}
        href={pending ? null : image.url}
        src={previewUrl ?? image.thumb_url}
      />
      <span className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-0.5">
        <span className="tw-text-sm tw-font-bold tw-text-spa-heading">{label}</span>
        {rejected ? (
          <span className="tw-text-xs tw-text-spa-error" role="alert">{REJECTED_MESSAGE}</span>
        ) : (
          <span className="tw-truncate tw-text-xs tw-text-spa-tertiary">
            {dragging ? `Drop to use as the ${label.toLowerCase()}` : caption}
          </span>
        )}
      </span>
      {onPick && (
        <>
          <input
            accept={ACCEPT}
            className="tw-sr-only"
            id={inputId}
            type="file"
            onChange={event => take(event.target.files?.[0])}
          />
          <SPAButton asChild size="xs">
            <label htmlFor={inputId}>{image.file_name || pending ? 'Replace' : 'Choose File'}</label>
          </SPAButton>
        </>
      )}
    </div>
  );
};

export const Divider = () => <div className="tw-h-px tw-bg-spa-border-secondary" />;

export const IMAGES_HINT = 'PNG, JPG, or GIF. Drop a file on a row or choose one.';
